import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { DollarSign, TrendingUp, TrendingDown, BanknoteIcon, Wallet, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /operations/pnl — Provider P&L tile (CFO buyer, Operations Suite).
 *
 * Blueprint Decision #11 enhancement. The CFO/RCM Director's
 * morning glance: revenue collected this period − payouts (paid +
 * pending) → net P&L + margin %, plus open A/R for context.
 */
export default function ProviderPnL() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('mtd');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/operations/pnl?period=${period}`, getAuthHeader());
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load P&L');
    } finally {
      setLoading(false);
    }
  }, [period, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const netPositive = (data?.net_pnl || 0) >= 0;

  return (
    <DashboardShell data-testid="provider-pnl-page">
      <header className="mb-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
          <DollarSign className="w-3 h-3" /> Operations · Finance
        </div>
        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Provider P&L</h1>
        <p className="text-sm text-slate-600 mt-1">
          Revenue, payouts, margin — the CFO morning glance. Numbers update in real-time from paid claims and clinician payouts.
        </p>
        <div className="flex gap-2 mt-4" data-testid="pnl-period-toggle">
          {['mtd', 'qtd', 'ytd'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              data-testid={`pnl-period-${p}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
                period === p
                  ? 'bg-cn-primary text-white border-cn-primary'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-cn-primary'
              }`}
            >
              {p.toUpperCase()}
            </button>
          ))}
        </div>
      </header>

      {loading ? (
        <div className="rounded-2xl bg-white border border-slate-100 p-8 text-slate-500">Loading P&L…</div>
      ) : !data ? (
        <div className="rounded-2xl bg-white border border-slate-100 p-8 text-slate-500">No data.</div>
      ) : (
        <div className="space-y-5">
          {/* HEADLINE TILES */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <PnLTile
              label="Revenue collected"
              value={fmt(data.revenue.gross_collected)}
              sublabel={`${data.revenue.paid_claim_count} paid claims`}
              icon={<TrendingUp className="w-5 h-5 text-emerald-600" />}
              testid="pnl-revenue"
            />
            <PnLTile
              label="Payouts (paid + pending)"
              value={fmt((data.expenses.payouts_paid || 0) + (data.expenses.payouts_pending || 0))}
              sublabel={`${data.expenses.payout_count} requests`}
              icon={<Wallet className="w-5 h-5 text-cn-primary" />}
              testid="pnl-payouts"
            />
            <PnLTile
              label="Net P&L"
              value={fmt(data.net_pnl)}
              sublabel={netPositive ? 'In the black' : 'In the red'}
              icon={netPositive ? <TrendingUp className="w-5 h-5 text-emerald-600" /> : <TrendingDown className="w-5 h-5 text-rose-600" />}
              testid="pnl-net"
              tone={netPositive ? 'positive' : 'negative'}
            />
            <PnLTile
              label="Margin"
              value={`${data.margin_pct}%`}
              sublabel="Net / revenue"
              icon={<BanknoteIcon className="w-5 h-5 text-amber-600" />}
              testid="pnl-margin"
            />
          </div>

          {/* DETAIL: BREAKDOWN */}
          <section className="rounded-2xl bg-white border border-slate-100 p-6" data-testid="pnl-breakdown">
            <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-4">Breakdown</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Revenue</div>
                <Row label="Gross collected" value={fmt(data.revenue.gross_collected)} />
                <Row label="Platform fees collected" value={fmt(data.revenue.platform_fees_collected)} />
                <Row label="Paid claims" value={`${data.revenue.paid_claim_count}`} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Expenses</div>
                <Row label="Payouts paid" value={fmt(data.expenses.payouts_paid)} />
                <Row label="Payouts pending" value={fmt(data.expenses.payouts_pending)} />
                <Row label="Payout requests" value={`${data.expenses.payout_count}`} />
              </div>
            </div>
          </section>

          {/* OPEN A/R */}
          <section className="rounded-2xl bg-white border border-slate-100 p-6" data-testid="pnl-ar">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Open receivables</h2>
            </div>
            <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{fmt(data.ar.open_ar)}</div>
            <p className="text-xs text-slate-500 mt-1">{data.ar.open_claim_count} open claims awaiting collection.</p>
          </section>

          <p className="text-[10px] text-slate-400">
            Generated {new Date(data.generated_at).toLocaleString()} · scope={data.scope} · period={data.period}
          </p>
        </div>
      )}
    </DashboardShell>
  );
}

function PnLTile({ label, value, sublabel, icon, testid, tone }) {
  const ring = tone === 'positive'
    ? 'border-emerald-200 bg-emerald-50/40'
    : tone === 'negative'
    ? 'border-rose-200 bg-rose-50/40'
    : 'border-slate-100 bg-white';
  return (
    <div className={`rounded-2xl border ${ring} p-5`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        {icon}
      </div>
      <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{sublabel}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-slate-600">{label}</span>
      <span className="font-semibold text-slate-900 tabular-nums">{value}</span>
    </div>
  );
}
